import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { getRentals, goToRentals } from '@/actions/rentals';
import MaxWidth from '@/components/common/layout/MaxWidth';
import NarrowWidth from '@/components/common/layout/NarrowWidth';
import { RentalsList } from '@/components/common/RentalsList/RentalsList';
import StarRating from '@/components/common/ui/StarRating';
import Collapsible from '@/components/landing-pages/Collapsible';
import CollapsibleRichText from '@/components/landing-pages/CollapsibleRichText';
import ContactSupport from '@/components/landing-pages/ContactSupport';
import ContentGrid from '@/components/landing-pages/ContentGrid';
import CoverageIcons from '@/components/landing-pages/CoverageIcons';
import HealthCareHeroes from '@/components/landing-pages/HealthCareHeroes';
import Hero from '@/components/landing-pages/Hero';
import HeroTwo from '@/components/landing-pages/HeroTwo';
import HowToBlock from '@/components/landing-pages/HowToBlock';
import OutdoorsyHistory from '@/components/landing-pages/OutdoorsyHistory';
import RichtextWithBg from '@/components/landing-pages/RichtextWithBg';
import { trackEvent } from '@/services/track-event';

import css from './LandingPageTemplate.module.css';

const LandingPageTemplate = ({
  metadata: { heading, hero_image, subheading, button_url, button_label, content_blocks } = {},
}) => {
  useEffect(() => {
    trackEvent({
      event: 'Locality : Viewed page',
      pageType: 'Landing page',
    });
  }, []);

  return (
    <div className={css.container}>
      <div className="relative flex flex-col items-center w-full">
        <div className="flex flex-col w-full max-w-xl p-0 md:px-4 lg:my-4">
          <HeroSection
            title={heading}
            subtitle={subheading}
            buttonUrl={button_url}
            buttonLabel={button_label}
            heroImg={hero_image}
          />
        </div>
        {content_blocks.map((content, i) => {
          if (typeof contentComponents[content.type] !== 'undefined') {
            const ContentComponent = contentComponents[content.type];

            return <ContentComponent key={i} value={content.value} />;
          }
          return null;
        })}
      </div>
      <div className="h-10 md:h-32">&nbsp;</div>
    </div>
  );
};

const HeroSection = ({ title, subtitle, buttonUrl, buttonLabel, heroImg }) => {
  if (title === 'How it works') {
    return (
      <HeroTwo
        title={title}
        subtitle={subtitle}
        buttonUrl={buttonUrl}
        buttonLabel={buttonLabel}
        heroImg={heroImg}
      />
    );
  } else {
    return (
      <Hero
        title={title}
        subtitle={subtitle}
        buttonUrl={buttonUrl}
        buttonLabel={buttonLabel}
        heroImg={heroImg}
      />
    );
  }
};

const howToRegex = new RegExp('(earn money renting on outdoorsy)|(renting an rv is easy)', 'i');
const Heading = ({ value }) => {
  if (howToRegex.test(value)) {
    return (
      <MaxWidth className="my-8">
        <h2 className="text-3xl font-medium">{value}</h2>
      </MaxWidth>
    );
  }
  return (
    <NarrowWidth className="my-8">
      <h2 className="text-3xl font-medium">{value}</h2>
    </NarrowWidth>
  );
};

const SplitHeading = ({ value }) => (
  <>
    <h2 className="text-2xl font-medium">{value}</h2>
    <SplitHr />
  </>
);

const Image = ({ value: { url, title } = {} }) => (
  <img className="md:rounded" src={url} alt={title} />
);

const SplitImage = ({ value: { url, title } = {} }) => (
  <img className="rounded" src={url} alt={title} />
);

const Hr = () => <hr className="w-32 m-auto mt-4 bg-transparent border-b border-gray-200" />;
const SplitHr = () => <hr className="w-24 my-2 bg-transparent border-b border-gray-200" />;

const Richtext = ({ value }) => {
  if (value.includes('COLLAPSABLE')) {
    return (
      <NarrowWidth>
        <CollapsibleRichText content={value} />
      </NarrowWidth>
    );
  }

  return (
    <NarrowWidth className={`mt-2 mb-6 w-full ${css.richtext}`}>
      <div className={`${css.maxCharacterCount} w-full`}>
        {value && <div dangerouslySetInnerHTML={{ __html: value }} />}
      </div>
    </NarrowWidth>
  );
};

const SplitRichtext = ({ value }) => (
  <div className={`mt-2 mb-6 w-full ${css.splitRichtext}`}>
    <div className={`${css.maxCharacterCount} `}>
      {value && <div dangerouslySetInnerHTML={{ __html: value }} />}
    </div>
  </div>
);

const Raw = ({ value }) => {
  if (value === '<div id="health-care-heroes" />') {
    return <HealthCareHeroes />;
  }

  if (value === '<div id="outdoorsy-history" />') {
    return <OutdoorsyHistory />;
  }

  if (value === '<div id="how-to-block-renter" />') {
    return <HowToBlock renter={true} />;
  }

  if (value === '<div id="how-to-block-owner" />') {
    return <HowToBlock renter={false} />;
  }

  return (
    <NarrowWidth className={`mt-2 mb-6 self-center ${css.richtext}`}>
      {value && <div dangerouslySetInnerHTML={{ __html: value }} />}
    </NarrowWidth>
  );
};

const SplitReviews = ({ value }) => (
  <div className={`mt-2 mb-6 self-center ${css.splitRichtext}`}>
    <div className="p-4 text-lg bg-gray-200 rounded">Some of our favorite reviews</div>
    <div className="p-4">
      {value.map(value => {
        return (
          <div className={`${css.maxCharacterCount}`} key={value.text}>
            <div className="flex justify-center">
              <StarRating starsSelected={value.rating} />
            </div>
            {value.text && (
              <div className="mt-1 mb-2 " dangerouslySetInnerHTML={{ __html: value.text }} />
            )}
            <p className="mt-2 font-medium">- {value.author}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const Reviews = ({ value }) => (
  <div className={`mt-20 mb-12 ${css.splitRichtext}`}>
    <div className="p-4 text-lg bg-gray-200 rounded">Some of our favorite reviews</div>
    <div className="p-4">
      {value.map(value => {
        return (
          <div className={`${css.maxCharacterCount}`} key={value.text}>
            <div className="flex justify-center">
              <StarRating starsSelected={value.rating} />
            </div>
            {value.text && (
              <div className="mt-1 mb-2 " dangerouslySetInnerHTML={{ __html: value.text }} />
            )}
            <p className="mt-2 font-medium">- {value.author}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const SplitListWithIcons = ({ value }) => (
  <div className="mt-2 mb-6">
    {value.map(v => {
      return (
        <div key={v.top_text} className="mb-4 grid grid-cols-10 gap-6">
          <div className="col-span-1">
            <img src={v.icon} alt="" className="w-auto" />
          </div>
          <div className="col-span-9">
            <h3 className="mb-2 text-lg font-medium">{v.top_text}</h3>
            <p dangerouslySetInnerHTML={{ __html: v.bottom_text }} />
          </div>
        </div>
      );
    })}
  </div>
);

const ListWithIcons = ({ value }) => (
  <div className={`px-6 m-auto mt-8 mb-6 md:mt-20 md:mb-12 ${css.listWithIcons}`}>
    {value.map(v => {
      return (
        <div key={v.top_text} className="mb-4 grid grid-cols-6 gap-2 md:grid-cols-10 md:gap-6">
          <div className="col-span-1">
            <img src={v.icon} alt="" className="w-auto" />
          </div>
          <div className="col-span-4 md:col-span-9">
            <h3 className="mb-2 text-lg font-medium">{v.top_text}</h3>
            <p dangerouslySetInnerHTML={{ __html: v.bottom_text }} />
          </div>
        </div>
      );
    })}
  </div>
);

const Split = ({ value: { left, right } = {} }) => {
  if (!left.length) {
    const findComponent = content =>
      typeof contentComponents[`split-${content.type}`] !== 'undefined'
        ? contentComponents[`split-${content.type}`]
        : Hr;

    const LeftComponent = findComponent(left);
    const RightComponent = findComponent(right);

    return (
      <NarrowWidth className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-16 md:mb-12">
        <LeftComponent value={left.value} />
        <RightComponent value={right.value} />
      </NarrowWidth>
    );
  }

  return (
    <NarrowWidth className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-16 md:mb-12">
      {left && (
        <div className="flex flex-col self-center">
          {left.map((content, i) => {
            if (typeof contentComponents[content.type] !== 'undefined') {
              const contentName = `split-${content.type}`;
              const ContentComponent = contentComponents[contentName];
              return <ContentComponent key={`${contentName}-${i}`} value={content.value} />;
            }
            return null;
          })}
        </div>
      )}
      {right && (
        <div className={`flex flex-col self-center ${css.rightContent}`}>
          {right.map((content, i) => {
            if (typeof contentComponents[content.type] !== 'undefined') {
              const contentName = `split-${content.type}`;
              const ContentComponent = contentComponents[contentName];
              return <ContentComponent key={`${contentName}-${i}`} value={content.value} />;
            }
            return null;
          })}
        </div>
      )}
    </NarrowWidth>
  );
};

const RvListings = () => {
  const dispatch = useDispatch();
  const getPageRentals = useCallback(filters => dispatch(getRentals(filters)), [dispatch]);

  useEffect(() => {
    getPageRentals({ count: 8 });
  }, [getPageRentals]);

  return (
    <MaxWidth className="mt-12 mb-12 md:mt-24">
      <RentalsList goToRentals={() => dispatch(goToRentals())} />
    </MaxWidth>
  );
};

const contentComponents = {
  heading: Heading,
  richtext: Richtext,
  image: Image,
  reviews: Reviews,
  split: Split,
  raw: Raw,
  collapsible: Collapsible,
  'rv-listing': RvListings,
  'split-image': SplitImage,
  'content-grid': ContentGrid,
  'split-heading': SplitHeading,
  'split-reviews': SplitReviews,
  'coverage-icons': CoverageIcons,
  'split-richtext': SplitRichtext,
  'list-with-icons': ListWithIcons,
  'contact-support': ContactSupport,
  'richtext-with-bg': RichtextWithBg,
  'split-list-with-icons': SplitListWithIcons,
};

export default LandingPageTemplate;
